import { Plus, Trash2 } from "lucide-react";
import { ImageInput } from "./ImageInput";
import { TEMPLATE_OPTIONS } from "../components/designer/templates";
import { mockupSrc } from "../lib/mockup";

const AREA_KEYS = [["x", "Left %"], ["y", "Top %"], ["w", "Width %"], ["h", "Height %"]];

const SideEditor = ({ p, s, j, set }) => {
  const setArea = (k, v) => set("sides", p.sides.map((x, n) => (n === j ? { ...x, area: { ...x.area, [k]: parseFloat(v) || 0 } } : x)));
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 rounded-lg border border-white/10 p-3" data-testid={`admin-product-side-${s.key}`}>
      <div className="relative aspect-square">
        <img src={mockupSrc(p, p.colors[0], s.key)} alt="" className="absolute inset-0 h-full w-full object-contain" />
        <div className="absolute border border-dashed border-[#60A5FA]" style={{ left: `${s.area.x}%`, top: `${s.area.y}%`, width: `${s.area.w}%`, height: `${s.area.h}%` }} />
      </div>
      <div>
        <p className="font-display text-lg font-bold uppercase">{s.label} print area</p>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {AREA_KEYS.map(([k, l]) => (
            <div key={k}><label className="text-[10px] uppercase text-slate-500">{l}</label><input className="field !px-2 !py-1.5" type="number" value={s.area[k]} onChange={(e) => setArea(k, e.target.value)} data-testid={`admin-product-${s.key}-area-${k}`} /></div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ColorRow = ({ p, c, j, set, hasBack }) => {
  const setC = (k, v) => set("colors", p.colors.map((x, n) => (n === j ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2 rounded-lg border border-white/10 p-3" data-testid={`admin-product-color-${j}`}>
      <div className="flex items-center gap-2">
        <input type="color" value={c.hex} onChange={(e) => setC("hex", e.target.value)} className="h-10 w-12 cursor-pointer rounded bg-transparent" data-testid={`admin-product-color-hex-${j}`} />
        <input className="field" value={c.name} onChange={(e) => setC("name", e.target.value)} data-testid={`admin-product-color-name-${j}`} />
        <button onClick={() => set("colors", p.colors.filter((_, n) => n !== j))} className="p-2 text-slate-500 hover:text-red-400" data-testid={`admin-product-color-delete-${j}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <p className="text-xs text-slate-500">Optional photos (leave empty to use the flat template in this color)</p>
      <div className="flex flex-wrap gap-4">
        <div><span className="text-xs text-slate-400">Front photo</span><ImageInput value={c.front_image} onChange={(v) => setC("front_image", v)} testId={`admin-product-color-${j}-front-photo`} /></div>
        {hasBack && <div><span className="text-xs text-slate-400">Back photo</span><ImageInput value={c.back_image} onChange={(v) => setC("back_image", v)} testId={`admin-product-color-${j}-back-photo`} /></div>}
      </div>
      {(c.front_image || c.back_image) && <button onClick={() => set("colors", p.colors.map((x, n) => (n === j ? { ...x, front_image: "", back_image: "" } : x)))} className="text-xs text-slate-400 hover:text-white">Use flat template instead</button>}
    </div>
  );
};

export const ProductEditor = ({ p, i, update, save, remove }) => {
  const set = (k, v) => update(p.id, k, v);
  const hasBack = p.sides.some((s) => s.key === "back");
  const toggleBack = () => set("sides", hasBack ? p.sides.filter((s) => s.key !== "back") : [...p.sides, { key: "back", label: "Back", area: { x: 30, y: 20, w: 40, h: 50 } }]);
  return (
    <div className="panel space-y-4 p-5" data-testid={`admin-product-${i}`}>
      <div className="grid gap-3 sm:grid-cols-[1fr_160px_90px]">
        <div><label className="label">Name</label><input className="field" value={p.name} onChange={(e) => set("name", e.target.value)} data-testid={`admin-product-name-${i}`} /></div>
        <div><label className="label">Mockup shape</label>
          <select className="field" value={p.template} onChange={(e) => set("template", e.target.value)} data-testid={`admin-product-template-${i}`}>
            {TEMPLATE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div><label className="label">Order</label><input className="field" type="number" value={p.order} onChange={(e) => set("order", parseInt(e.target.value, 10) || 0)} /></div>
      </div>
      <div><label className="label">Price note (shown to customers)</label><input className="field" value={p.price_note} onChange={(e) => set("price_note", e.target.value)} data-testid={`admin-product-price-${i}`} /></div>
      <div className="flex flex-wrap gap-5 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-[#E5202A]" checked={p.active} onChange={(e) => set("active", e.target.checked)} data-testid={`admin-product-active-${i}`} />Show in Design Studio</label>
        <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-[#E5202A]" checked={hasBack} onChange={toggleBack} data-testid={`admin-product-back-${i}`} />Has a back side</label>
      </div>
      {p.colors.length > 0 && p.sides.map((s, j) => <SideEditor key={s.key} p={p} s={s} j={j} set={set} />)}
      <p className="label">Colors</p>
      {p.colors.map((c, j) => <ColorRow key={j} p={p} c={c} j={j} set={set} hasBack={hasBack} />)}
      <button onClick={() => set("colors", [...p.colors, { name: "New Color", hex: "#888888", front_image: "", back_image: "" }])} className="flex items-center gap-1 text-sm text-[#60A5FA]" data-testid={`admin-product-add-color-${i}`}><Plus className="h-4 w-4" />Add color</button>
      <div className="flex justify-between border-t border-white/10 pt-3">
        <button onClick={() => window.confirm("Delete this product?") && remove(p.id)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-red-400" data-testid={`admin-product-delete-${i}`}><Trash2 className="h-4 w-4" />Delete</button>
        <button onClick={() => (p.colors.length ? save(p) : null)} disabled={!p.colors.length} className="btn-red !py-2 !text-base" data-testid={`admin-product-save-${i}`}>Save</button>
      </div>
    </div>
  );
};
